import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard,
    People,
    Group,
    Schedule,
    Note,
    Receipt,
    AccountCircle,
    Logout,
    SupervisorAccount as SupervisorAccountIcon,
    AdminPanelSettings,
    AccessTime,
    AssignmentTurnedIn
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserPrivilegeLevel } from '../../utils/roleUtils';
import SystemHealthIndicator from './SystemHealthIndicator';

interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    minPrivilegeLevel: number;
    maxPrivilegeLevel?: number;
    description: string;
}

const Navbar: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const theme = useTheme();
    // The permanent drawer is only shown at `lg` and up so tablets / small
    // laptops have full horizontal space for tables. Below `lg`, the drawer
    // becomes a temporary overlay opened by the hamburger button.
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileOpen, setMobileOpen] = useState(false);

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        await logout();
        handleClose();
        navigate('/login');
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Helper function to check if user has required role
    const hasRole = (requiredRoles: string[]): boolean => {
        if (!user) return false;

        // Check both the roles array (for .NET backend) and user_type (for Django backend)
        const userRoles = user.roles || [user.user_type];
        return requiredRoles.some(role => userRoles.includes(role));
    };

    // Helper function to get user's highest privilege level
    const privilegeLevel = getUserPrivilegeLevel(user);

    const menuItems: MenuItem[] = [
        {
            text: 'Dashboard',
            icon: <Dashboard />,
            path: '/dashboard',
            minPrivilegeLevel: 2, // Care Coordinator and above only
            description: 'View system overview and key metrics'
        },
        {
            text: 'Clients',
            icon: <People />,
            path: '/clients',
            minPrivilegeLevel: 2, // Care Coordinator and above only
            description: 'Manage client information and care plans'
        },
        {
            text: 'Staff Management',
            icon: <Group />,
            path: '/staff',
            minPrivilegeLevel: 3, // Manager and Admin only
            description: 'Manage staff profiles and user accounts'
        },
        {
            text: 'Roster',
            icon: <Schedule />,
            path: '/roster',
            minPrivilegeLevel: 2, // Care Coordinator and above
            description: 'View and manage staff schedules'
        },
        // {
        //     text: 'Time Clock',
        //     icon: <AccessTime />,
        //     path: '/time-clock',
        //     minPrivilegeLevel: 1, // All staff can use time clock
        //     description: 'Clock in and out for scheduled shifts'
        // },
        {
            text: 'User Shifts',
            icon: <Schedule />,
            path: '/user-shifts',
            minPrivilegeLevel: 1, // All staff can view their shifts
            maxPrivilegeLevel: 1, // Only basic staff (not coordinators or managers)
            description: 'View all your scheduled shifts and history'
        },
        {
            text: 'Shift Management',
            icon: <AssignmentTurnedIn />,
            path: '/roster/shift-management',
            minPrivilegeLevel: 3, // All staff can complete shifts, admins can review
            description: 'Complete shifts and review completed work'
        },
        {
            text: 'Notes',
            icon: <Note />,
            path: '/notes',
            minPrivilegeLevel: 2, // Care Coordinator and above only
            description: 'Access client care notes and documentation'
        },
        {
            text: 'Client Invoices',
            icon: <Receipt />,
            path: '/billing/client-invoices',
            minPrivilegeLevel: 3,
            description: 'View and manage client invoices'
        },
        {
            text: 'Billing Rates',
            icon: <Receipt />,
            path: '/billing/rates',
            minPrivilegeLevel: 3,
            description: 'Manage staff pay rates and client charge rates'
        },
        {
            text: 'Payroll',
            icon: <Receipt />,
            path: '/payroll',
            minPrivilegeLevel: 3, // Manager and Admin only
            description: 'Manage payroll, approvals, and payments'
        },
        {
            text: 'Role Test',
            icon: <Dashboard />,
            path: '/role-test',
            minPrivilegeLevel: 3, // Available to all users for testing
            description: 'Test role-based access control'
        },
    ];

    // Admin-only features
    const adminMenuItems: MenuItem[] = privilegeLevel >= 4 ? [
        {
            text: 'System Admin',
            icon: <AdminPanelSettings />,
            path: '/admin',
            minPrivilegeLevel: 4,
            description: 'System administration and configuration'
        }
    ] : [];

    const filteredMenuItems = [...menuItems, ...adminMenuItems].filter(item => {
        const hasMinPrivilege = privilegeLevel >= item.minPrivilegeLevel;
        const hasMaxPrivilege = !item.maxPrivilegeLevel || privilegeLevel <= item.maxPrivilegeLevel;
        return hasMinPrivilege && hasMaxPrivilege;
    });

    const drawer = (
        <div>
            <Toolbar />
            <List>
                {filteredMenuItems.map((item) => (
                    <ListItem key={item.text} disablePadding>
                        <ListItemButton
                            selected={location.pathname === item.path}
                            onClick={() => {
                                navigate(item.path);
                                if (isMobile) {
                                    setMobileOpen(false);
                                }
                            }}
                            title={item.description}
                        >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            <ListItemText
                                primary={item.text}
                                secondary={privilegeLevel >= 4 ? item.description : undefined}
                                secondaryTypographyProps={{
                                    fontSize: '0.75rem',
                                    color: 'text.secondary'
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </div>
    );

    // Don't show navbar on auth pages
    if (!user && (location.pathname === '/login' || location.pathname === '/register')) {
        return null;
    }

    return (
        <>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <Toolbar>
                    {user && (
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{ mr: 2, display: { lg: 'none' } }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Felice Care
                        {user && (
                            <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                                {user.roles?.[0] || user.user_type} Access
                            </Typography>
                        )}
                    </Typography>

                    {/* System Health Indicator */}
                    <SystemHealthIndicator />

                    {user ? (
                        <div>
                            <IconButton
                                size="large"
                                aria-label="account of current user"
                                aria-controls="menu-appbar"
                                aria-haspopup="true"
                                onClick={handleMenu}
                                color="inherit"
                            >
                                <Avatar sx={{ width: 32, height: 32 }}>
                                    {user.first_name?.[0]}
                                </Avatar>
                            </IconButton>
                            <Menu
                                id="menu-appbar"
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                }}
                                keepMounted
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'right',
                                }}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                                    <AccountCircle sx={{ mr: 1 }} />
                                    Profile
                                </MenuItem>
                                <MenuItem onClick={handleLogout}>
                                    <Logout sx={{ mr: 1 }} />
                                    Logout
                                </MenuItem>
                            </Menu>
                        </div>
                    ) : (
                        <Box>
                            <Button color="inherit" onClick={() => navigate('/login')}>
                                Login
                            </Button>
                            {/* <Button color="inherit" onClick={() => navigate('/register')}>
                                Register
                            </Button> */}
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            {user && (
                <>
                    {/* Desktop drawer (lg and up) */}
                    <Drawer
                        variant="permanent"
                        sx={{
                            display: { xs: 'none', lg: 'block' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: 260,
                            },
                        }}
                        open
                    >
                        {drawer}
                    </Drawer>

                    {/* Mobile/tablet drawer (below lg) */}
                    <Drawer
                        variant="temporary"
                        open={mobileOpen}
                        onClose={handleDrawerToggle}
                        ModalProps={{
                            keepMounted: true, // Better open performance on mobile.
                        }}
                        sx={{
                            display: { xs: 'block', lg: 'none' },
                            '& .MuiDrawer-paper': {
                                boxSizing: 'border-box',
                                width: 260,
                            },
                        }}
                    >
                        {drawer}
                    </Drawer>
                </>
            )}
        </>
    );
};

export default Navbar;
